import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import Box from '@quintoandar/cozy-core/Box';
import Typography from '@quintoandar/cozy-core/Typography';
import Divider from '@quintoandar/cozy-core/Divider';

import FlexCounter from 'components/FlexCounter';
import FlexChoices from 'components/FlexChoices';
import ChipBoolean, { CHOICES } from 'components/ChipBoolean';
import SpacedInput from 'components/SpacedInput';
import ChoiceRadio from 'components/ChoiceRadio';
import ChecklistTitleWrapper from 'containers/Checklist/ChecklistTitleWrapper';

import { ITEM_TYPES, ITEM_TYPES_ARRAY } from './constants';
import { getMessageByIntlKey } from './getMessageByIntlKey';
import messages from './messages';

const components = {
  [ITEM_TYPES.CHOICE]: FlexChoices,
  [ITEM_TYPES.BOOLEAN]: ChipBoolean,
  [ITEM_TYPES.TEXT]: SpacedInput,
  [ITEM_TYPES.INTEGER]: FlexCounter,
  [ITEM_TYPES.RADIO]: ChoiceRadio,
};

const isChoice = type =>
  type === ITEM_TYPES.CHOICE ||
  type === ITEM_TYPES.BOOLEAN ||
  type === ITEM_TYPES.RADIO;

export const addChoiceParams = ({ type, item, itemSlug }) => {
  if (!isChoice(type)) {
    return {};
  }

  const { choiceValues } = item;
  return {
    choiceValues,
    groupName: itemSlug,
  };
};

const getValue = ({ type, event }) => {
  switch (type) {
    case ITEM_TYPES.INTEGER:
      return event.value;

    case ITEM_TYPES.BOOLEAN:
      return event.target.value === CHOICES.YES;

    default:
      return event.target.value;
  }
};

export const onChangeComponent = ({
  itemSlug,
  previousValue,
  onChangeItem,
  type,
}) => event => {
  const onChangeItemParam = {
    value: getValue({ type, event }),
    previousValue,
    itemSlug,
  };
  onChangeItem(onChangeItemParam);
};

const buildComponentParams = ({
  item,
  onChangeParams,
  type,
  itemSlug,
  onValidateItem,
  shouldValidate,
}) => ({
  onChange: onChangeComponent(onChangeParams),
  label: item.label,
  value: item.value,
  intlKey: item.key,
  id: itemSlug,
  onValidateItem,
  shouldValidate,
  ...addChoiceParams({ type, item, itemSlug }),
});

const addComponent = ({
  items,
  onChangeItem,
  onValidateItem,
  shouldValidate,
  children,
}) => itemSlug => {
  const type = items[itemSlug].type;
  let item = items[itemSlug];

  if (ITEM_TYPES.INTEGER === type && item.optional) {
    item = { ...item, value: item.value || 0 };
  }
  const onChangeParams = {
    itemSlug,
    previousValue: item.value,
    onChangeItem,
    type,
  };

  const componentParams = buildComponentParams({
    item,
    onChangeParams,
    type,
    itemSlug,
    onValidateItem: onValidateItem({ itemSlug }),
    shouldValidate,
  });

  const ComponentToRender = components[type];
  const component = (
    <ComponentToRender
      key={componentParams.value}
      {...componentParams}
    >
      {children}
    </ComponentToRender>
  );

  return {
    component,
    ...item,
  };
};

const labelByLexicographicalOrder = items => (key1, key2) =>
  items[key1] &&
  items[key1].label &&
  items[key2] &&
  items[key2].label &&
  items[key1].label.localeCompare(items[key2].label);

const addComponentsToItems = ({
  items,
  onChangeItem,
  onValidateItem,
  shouldValidate,
  children,
}) =>
  Object.keys(items)
    .filter(itemSlug => items[itemSlug].type)
    .sort(labelByLexicographicalOrder(items))
    .map(
      addComponent({
        items,
        onChangeItem,
        onValidateItem,
        shouldValidate,
        children,
      }),
    );

const renderComponents = (item, index, items) => (
  <Box key={item.key}>
    {item.component}
    {items.length !== index + 1 && <Divider />}
  </Box>
);

const renderItems = ({
  items,
  onChangeItem,
  onValidateItem,
  shouldValidate,
  children,
}) => {
  const itemsWithComponents = addComponentsToItems({
    items,
    onChangeItem,
    onValidateItem,
    shouldValidate,
    children,
  });

  return itemsWithComponents.map(renderComponents);
};

export const Section = ({
  section,
  onChangeItem,
  onValidateSection,
  shouldValidate,
  children,
  intlKey,
  intl,
}) => {
  const [itemsValidity, setItemsValidity] = useState({});

  const isSectionValid = useMemo(
    () => Object.values(itemsValidity).every(Boolean),
    [itemsValidity]
  );

  const onValidateItem = useCallback(({ itemSlug }) => isValid => {
    if (itemsValidity[itemSlug] !== isValid) {
      setItemsValidity({ ...itemsValidity, [itemSlug]: isValid });
    }
  }, [itemsValidity]);

  useEffect(() => {
    onValidateSection(isSectionValid);
  }, [isSectionValid]);

  return useMemo(
    () => (
      <Fragment>
        {section.isHeading ? (
          <ChecklistTitleWrapper mb={3}>
            <Typography level="heading">
              {intl.formatMessage(
                getMessageByIntlKey({ availableMessages: messages, intlKey }),
              )}
            </Typography>
          </ChecklistTitleWrapper>
        ) : (
          <Box mt={4} mb={3}>
            <Typography level="title">
              {intl.formatMessage(
                getMessageByIntlKey({ availableMessages: messages, intlKey }),
              )}
            </Typography>
          </Box>
        )}
        {section.items &&
          renderItems({
            items: section.items,
            onChangeItem,
            onValidateItem,
            shouldValidate,
            children,
          })}
      </Fragment>
    ),
    [section, shouldValidate],
  );
};

Section.propTypes = {
  section: PropTypes.shape({
    index: PropTypes.number,
    items: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      type: PropTypes.oneOf(ITEM_TYPES_ARRAY),
      choiceValues: PropTypes.array,
    }).isRequired,
  }).isRequired,
  onChangeItem: PropTypes.func,
  onValidateSection: PropTypes.func,
  shouldValidate: PropTypes.bool,
  intlKey: PropTypes.string,
  intl: PropTypes.object,
};

export default injectIntl(Section);
